import type { IMetricsProgress } from '../../api/metrics-updates-contracts';
import { StateCards, type IStateSignal } from '../../components/state-cards';

/**
 * The collection stage (SR-315 — the pipeline of WF-07, SR-482's tile for this stage): its totals
 * over the ticked editions — received of in scope, a bar, drawn in the page's summary line — and
 * the stage's four worklists as the page's navigation, each a tile with survey-api's count and what
 * to do with it: the advisors to ask (the list the page opens on), those whose firm has no status, the firms whose
 * files are awaited, the exceptions. The totals are text and press nothing; a tile is a link,
 * the open one marked (`aria-current`) the way a tab is. A survey whose firm is the respondent
 * (RIA_FIRM) draws the first and last tiles alone.
 */

export type StageView = 'direct' | 'unknown' | 'firms' | 'exceptions';

export const STAGE_VIEWS: readonly StageView[] = ['direct', 'unknown', 'firms', 'exceptions'];

/** Each list's card: its label, what to do with it, its count. */
export const STAGE_TILES: Record<StageView, { label: string; hint: string; count: (progress: IMetricsProgress) => number }> = {
  direct: { label: 'Owed by advisors', hint: 'ask them directly', count: (p) => p.owedDirect },
  unknown: { label: 'Firm not known', hint: 'set the firm’s status', count: (p) => p.owedUnknownFirm },
  firms: { label: 'Owed by firm files', hint: 'files from their firms', count: (p) => p.owedViaFirm },
  exceptions: { label: 'Exceptions', hint: 'ranked before, now under', count: (p) => p.exceptionsOpen }
};

/** The stage's totals over the ticked editions in a few words — "3 of 25 sent" — and a bar. */
export function StageTotals({ progress }: { progress: Nullable<IMetricsProgress> }) {
  const share = progress && progress.inScope > 0 ? Math.round((progress.received / progress.inScope) * 100) : 0;
  return (
    <span className="flex min-w-48 flex-1 items-center gap-2 text-sm" data-stage-totals>
      {progress ? (
        <span className="whitespace-nowrap">
          <strong className="tabular-nums text-[var(--color-teal)]">
            {progress.received.toLocaleString('en-US')}
          </strong>{' '}
          of <strong className="tabular-nums">{progress.inScope.toLocaleString('en-US')}</strong> sent
        </span>
      ) : (
        <span className="text-muted-foreground">Counting…</span>
      )}
      <span className="h-1.5 min-w-24 flex-1 overflow-hidden rounded-full bg-[var(--veritant-royal-15)]" aria-hidden>
        <span className="block h-full bg-[var(--color-teal)]" style={{ width: `${share}%` }} />
      </span>
    </span>
  );
}

export function StageNav({
  progress,
  view,
  views,
  hrefFor,
  signals
}: {
  progress: Nullable<IMetricsProgress>;
  view: StageView;
  views: readonly StageView[];
  hrefFor: (view: StageView) => string;
  signals: Nullable<Record<string, IStateSignal[]>>;
}) {
  return (
    <section aria-label="Collection stage" className="grid grid-cols-[minmax(0,1fr)] gap-3" data-stage-strip>
      <StateCards
        label="Metrics updates lists"
        cards={views.map((key) => ({
          key,
          to: hrefFor(key),
          replace: true,
          current: key === view,
          label: STAGE_TILES[key].label,
          count: progress ? STAGE_TILES[key].count(progress).toLocaleString('en-US') : '—',
          hint: STAGE_TILES[key].hint,
          signals: signals?.[key],
          share: progress && key !== 'exceptions' && progress.inScope > 0
            ? STAGE_TILES[key].count(progress) / progress.inScope
            : undefined,
          data: { 'data-stage': key },
          countData: { 'data-stage-count': '' }
        }))}
      />
    </section>
  );
}
